import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  BarChart3,
  Brain,
  FlaskConical,
  PencilLine,
  RotateCcw,
  Target,
  TrendingUp,
} from "lucide-react";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";

const STEPS: Array<{ icon: typeof Target; num: string; title: string; body: string }> = [
  {
    icon: Target,
    num: "01",
    title: "Diagnóstico calibrado",
    body: "Você começa por uma amostra de questões reais Fuvest balanceadas por disciplina. Medimos proficiência por tema, habilidade cognitiva e velocidade — não só acerto bruto.",
  },
  {
    icon: BarChart3,
    num: "02",
    title: "Engenharia reversa da banca",
    body: "10 anos de prova Fuvest classificados em taxonomia de 9 níveis. Sabemos quais temas voltam, quais distratores a banca usa, e o peso real de cada disciplina para Engenharia de Produção.",
  },
  {
    icon: TrendingUp,
    num: "03",
    title: "Plano adaptativo",
    body: "Cada semana prioriza o que dá mais ganho de nota: peso na prova × sua lacuna × tempo até a prova × peso do curso. Recalibra conforme você avança.",
  },
  {
    icon: PencilLine,
    num: "04",
    title: "Redação Fuvest",
    body: "Correção com a rubrica oficial em 4 critérios. Detector de cópia da coletânea, paráfrase e modelo pronto. Plano de reescrita concreto a cada entrega.",
  },
  {
    icon: FlaskConical,
    num: "05",
    title: "Discursivas da 2ª fase",
    body: "Correção item a item por disciplina, comparada com a abordagem esperada Fuvest. Identifica lacuna conceitual e classifica o tipo de erro — conteúdo, cálculo, interpretação ou comunicação.",
  },
  {
    icon: RotateCcw,
    num: "06",
    title: "Revisão espaçada",
    body: "Questões que você errou voltam no momento certo da curva de esquecimento (algoritmo SM-2). O que você domina sai do radar; o que escapa, persiste.",
  },
];

export function UspMetodoPage() {
  return (
    <div className="relative overflow-hidden">
      <section className="relative overflow-hidden bg-mesh">
        <Blob color="sky" className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-25 blur-2xl animate-blob" />
        <SoftStar color="mint" className="pointer-events-none absolute right-12 bottom-20 w-20 opacity-50 animate-float" />

        <Container size="wide" className="relative pt-16 pb-20 md:pt-24">
          <Link
            to="/usp"
            className="inline-flex items-center gap-1.5 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] transition-colors hover:text-[color:var(--color-foreground)]"
          >
            <ArrowLeft className="size-3.5" />
            FuvestPro USP
          </Link>

          <div className="mt-8 max-w-3xl animate-fade-up">
            <Badge className="gap-1.5" style={{ backgroundColor: "var(--color-accent)", color: "white", border: "none" }}>
              <Brain className="size-3" />
              Método FuvestPro
            </Badge>
            <h1 className="mt-7 font-display text-[length:var(--text-display-xl)] font-semibold leading-[var(--text-display-xl--line-height)] tracking-[var(--text-display-xl--letter-spacing)] text-balance">
              Não estude Fuvest no escuro. <span className="text-gradient-accent">Estude o padrão.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] leading-[var(--text-body-lg--line-height)] text-pretty">
              A Fuvest tem assinatura: leitura crítica em primeiro plano, contextualização dominante,
              distratores desenhados para separar quem sabe de quem chuta bem. O FuvestPro transforma
              essa assinatura em treino dirigido — não em mais um banco de questões genérico.
            </p>
          </div>
        </Container>
      </section>

      <section className="border-t border-[--color-border] py-20 md:py-24">
        <Container size="wide">
          <ol className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {STEPS.map(({ icon: Icon, num, title, body }) => (
              <li
                key={num}
                className="rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7 shadow-[var(--shadow-sm)] lift"
              >
                <div className="flex items-center justify-between">
                  <span className="chip size-10" style={{ backgroundColor: "var(--color-accent-soft)", color: "var(--color-accent-deep)" }}>
                    <Icon className="size-5" aria-hidden />
                  </span>
                  <span data-tabular="true" className="font-mono text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                    {num}
                  </span>
                </div>
                <h2 className="mt-5 font-display text-[length:var(--text-body-lg)] font-semibold leading-tight">
                  {title}
                </h2>
                <p className="mt-3 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)] leading-relaxed">
                  {body}
                </p>
              </li>
            ))}
          </ol>

          <div className="mt-14 rounded-3xl bg-mesh p-10 text-center md:p-14">
            <h2 className="font-display text-[length:var(--text-display-md)] font-semibold leading-tight tracking-[var(--text-display-md--letter-spacing)] text-balance">
              Veja o método na prática.
            </h2>
            <p className="mt-3 mx-auto max-w-xl text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
              Faça o demo do diagnóstico com questões reais Fuvest, ou explore a análise da banca.
            </p>
            <div className="mt-7 flex flex-col items-stretch gap-3 sm:flex-row sm:justify-center sm:gap-4">
              <Button asChild size="lg" variant="primary">
                <Link to="/usp/diagnostico">
                  Fazer o demo
                  <ArrowUpRight className="size-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/usp/banca-fuvest">
                  <BarChart3 className="size-4" />
                  Análise da banca
                </Link>
              </Button>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
